import { useVariantFeatureFlag } from "./useFeatureFlag";
import { FeatureFlags } from "../launchDarklyUtils";

/**
 * Variations for the `patientResultsTracker` experiment.
 *
 * Two tracker styles (minimal vs detailed) crossed with whether the estimated
 * results date is shown, plus a control (no tracker). LaunchDarkly buckets each
 * patient into exactly one of these at the configured rollout percentages.
 */
export enum ResultsTrackerVariant {
  Control = "control",
  MinimalShowDate = "minimal_show_date",
  MinimalHideDate = "minimal_hide_date",
  DetailedShowDate = "detailed_show_date",
  DetailedHideDate = "detailed_hide_date",
}

export type ResultsTrackerMode = "minimal" | "detailed";

export interface ResultsTrackerConfig {
  /** Whether to render the tracker at all (false for control / unknown variants). */
  enabled: boolean;
  mode: ResultsTrackerMode;
  /** Whether the estimated results date is shown on the tracker. */
  showEstimatedDate: boolean;
}

/**
 * Translate a raw `patientResultsTracker` variant string into a typed config.
 * Unknown values and `control` resolve to a disabled tracker (existing UI).
 */
export const getResultsTrackerConfig = (
  variant: string,
): ResultsTrackerConfig => {
  switch (variant) {
    case ResultsTrackerVariant.MinimalShowDate:
      return { enabled: true, mode: "minimal", showEstimatedDate: true };
    case ResultsTrackerVariant.MinimalHideDate:
      return { enabled: true, mode: "minimal", showEstimatedDate: false };
    case ResultsTrackerVariant.DetailedShowDate:
      return { enabled: true, mode: "detailed", showEstimatedDate: true };
    case ResultsTrackerVariant.DetailedHideDate:
      return { enabled: true, mode: "detailed", showEstimatedDate: false };
    default:
      return { enabled: false, mode: "minimal", showEstimatedDate: false };
  }
};

export interface ResolvedTrackerConfig extends ResultsTrackerConfig {
  /** The effective variant string from LaunchDarkly. */
  variant: string;
  loading: boolean;
}

/**
 * Resolve the patient results tracker experiment into a typed config.
 */
export const useResultsTrackerConfig = (): ResolvedTrackerConfig => {
  const { variant, loading } = useVariantFeatureFlag(
    FeatureFlags.PatientResultsTracker,
  );
  return {
    variant,
    loading,
    ...getResultsTrackerConfig(variant),
  };
};
